import type { SubscriptionSummary } from "../types/bookingCreation";

/**
 * Which actions a kolula KEY subscriber may take on a subscription — the ONE
 * definition in the end-user app, read by the bookings page (`OrdersPage`) and
 * the device-independent manage page (`SubscriptionManagePage`). Plan:
 * `docs/plans/kolula-key-subscription-b2c-obligations.md`, PR 4 and PR 5.
 *
 * - Cancel (§ 312k BGB): an active subscription not yet scheduled to end. The
 *   cancellation takes effect at the end of the paid period (plan O1).
 * - Withdraw (§ 356a BGB): an active subscription while the backend still
 *   accepts a withdrawal. `withdrawableUntil` is the backend's one rule
 *   (new-backend/app `lib/subscriptionWithdrawal.ts`) — never re-derived here.
 *   A scheduled cancellation does not end the withdrawal right, so a
 *   subscriber who cancelled can still withdraw inside the period; the cancel
 *   route accepts that.
 */
export type SubscriptionActionInput = Pick<
  SubscriptionSummary,
  "status" | "cancelAtPeriodEnd" | "withdrawableUntil" | "stripeSubscriptionId"
>;

export interface SubscriptionActions {
  /** Active and not yet scheduled to end: "Jetzt kündigen" is offered. */
  canCancel: boolean;
  /** Inside the withdrawal period: "Vertrag widerrufen" is offered. */
  canWithdraw: boolean;
  /** Active, but already cancelled to the end of the period. */
  isScheduledToEnd: boolean;
  /** No longer active (canceled, expired, failed, …). */
  isInactive: boolean;
  /**
   * Active but free (no Stripe subscription, e.g. a 100 % code): the cancel
   * route refuses it, so neither action is offered — the page says so instead.
   */
  isFree: boolean;
}

export const getSubscriptionActions = (
  subscription: SubscriptionActionInput,
  now: Date = new Date()
): SubscriptionActions => {
  const status = String(subscription.status ?? "").toLowerCase();
  const isActive = status === "active";
  // The withdrawal right does not depend on payment status: a subscription
  // with a failed or pending payment is still a contract inside its 14 days,
  // and the cancel route accepts a withdrawal for every status but canceled.
  const hasEnded = ["canceled", "expired", "incomplete_expired"].includes(status);
  const isFree = !hasEnded && !subscription.stripeSubscriptionId;
  const isScheduledToEnd = isActive && !isFree && subscription.cancelAtPeriodEnd === true;

  const until = subscription.withdrawableUntil
    ? Date.parse(subscription.withdrawableUntil)
    : Number.NaN;
  const insideWithdrawalPeriod =
    !Number.isNaN(until) && now.getTime() <= until;

  return {
    canCancel: isActive && !isFree && !isScheduledToEnd,
    canWithdraw: !hasEnded && !isFree && insideWithdrawalPeriod,
    isScheduledToEnd,
    isInactive: !isActive,
    isFree,
  };
};
